export function isSchoolLicensedUser(userData = {}) {
  const licenseType = String(userData.schoolLicenseType || '').toLowerCase();
  if (['site', 'multi-seat'].includes(licenseType)) {
    return true;
  }

  const tags = Array.isArray(userData.tags) ? userData.tags : [];
  return tags.some((tag) => ['School Site License', 'School Multi-Seat License'].includes(tag));
}

export function isAdminUser(userData = {}) {
  return userData?.settings?.role === 'admin';
}

export function isFreeUser(userData = {}) {
  return !userData.premium && !userData.trial;
}

export function isTrialUser(userData = {}) {
  return Boolean(userData.trial) && !userData.premium;
}

export function isPremiumPersonalUser(userData = {}) {
  return Boolean(userData.premium) && !isSchoolLicensedUser(userData);
}

function getBillingPeriodFromUserData(userData = {}) {
  const billingPeriod = String(
    userData.personalSubscriptionBilling?.billingPeriod
    || userData.metaSubscriptionTracking?.billingPeriod
    || '',
  ).toLowerCase();
  return billingPeriod === 'annual' || billingPeriod === 'yearly' ? 'annual' : 'monthly';
}

export function isMonthlySubscriber(userData = {}) {
  if (!isPremiumPersonalUser(userData)) return false;
  return getBillingPeriodFromUserData(userData) === 'monthly';
}

export function isYearlySubscriber(userData = {}) {
  if (!isPremiumPersonalUser(userData)) return false;
  return getBillingPeriodFromUserData(userData) === 'annual';
}

export function isSiteLicenseUser(userData = {}) {
  return isSchoolLicensedUser(userData);
}

export function isCompedUser(userData = {}) {
  return Boolean(userData.premium)
    && !isSchoolLicensedUser(userData)
    && !String(userData.personalSubscriptionBilling?.stripeSubscriptionId || '').trim();
}

export function isLifetimeUser(userData = {}) {
  return Boolean(userData.premium)
    && !isSchoolLicensedUser(userData)
    && !String(userData.stripeCustomerId || '').trim();
}

function normalizeEmailList(emails = []) {
  return (Array.isArray(emails) ? emails : String(emails || '').split(/[\n,;]+/))
    .map((email) => String(email || '').trim().toLowerCase())
    .filter(Boolean);
}

function normalizeIdList(ids = []) {
  return (Array.isArray(ids) ? ids : String(ids || '').split(/[\n,;]+/))
    .map((id) => String(id || '').trim())
    .filter(Boolean);
}

function matchesSpecificUsers(userData = {}, showTo = {}) {
  const uid = String(userData.uid || '').trim();
  const email = String(userData.email || '').trim().toLowerCase();
  const userIds = normalizeIdList(showTo.userIds);
  const emails = normalizeEmailList(showTo.emails);

  if (userIds.length && userIds.includes(uid)) return true;
  if (emails.length && email && emails.includes(email)) return true;
  return false;
}

function anyChecked(flags = {}) {
  return Object.values(flags).some(Boolean);
}

function matchesAccountStatus(userData = {}, accountStatus = {}) {
  if (!anyChecked(accountStatus)) return true;

  if (accountStatus.includeFree && isFreeUser(userData)) return true;
  if (accountStatus.includeTrial && isTrialUser(userData)) return true;
  if (accountStatus.includePremium && isPremiumPersonalUser(userData)) return true;
  return false;
}

function matchesSubscription(userData = {}, subscription = {}) {
  if (!anyChecked(subscription)) return true;

  if (subscription.includeMonthly && isMonthlySubscriber(userData)) return true;
  if (subscription.includeYearly && isYearlySubscriber(userData)) return true;
  if (subscription.includeSiteLicense && isSiteLicenseUser(userData)) return true;
  if (subscription.includeLifetime && isLifetimeUser(userData)) return true;
  if (subscription.includeComped && isCompedUser(userData)) return true;
  return false;
}

function getAnalyticsField(userData = {}, key) {
  if (userData[key] !== undefined) return userData[key];
  return userData.analytics?.[key];
}

function matchesEngagementCriteria(userData = {}, engagement = {}) {
  if (!engagement || !Object.keys(engagement).length) return true;

  if (engagement.premiumFalse && userData.premium === true) return false;
  if (engagement.schoolLicensedFalse && isSchoolLicensedUser(userData)) return false;

  const trialDaysRemainingMax = Number(engagement.trialDaysRemainingMax);
  if (Number.isFinite(trialDaysRemainingMax)) {
    const daysRemaining = Number(getAnalyticsField(userData, 'trialDaysRemaining'));
    if (!Number.isFinite(daysRemaining) || daysRemaining > trialDaysRemainingMax) return false;
  }

  const minActivationScore = Number(engagement.minActivationScore);
  if (Number.isFinite(minActivationScore) && Number(getAnalyticsField(userData, 'activationScore')) < minActivationScore) {
    return false;
  }

  const minInvestmentScore = Number(engagement.minInvestmentScore);
  if (Number.isFinite(minInvestmentScore) && Number(getAnalyticsField(userData, 'investmentScore')) < minInvestmentScore) {
    return false;
  }

  if (Array.isArray(engagement.conversionLikelihoodCategories) && engagement.conversionLikelihoodCategories.length) {
    const category = String(getAnalyticsField(userData, 'conversionLikelihoodCategory') || '');
    if (!engagement.conversionLikelihoodCategories.includes(category)) return false;
  }

  if (Array.isArray(engagement.trialExpirationRiskCategories) && engagement.trialExpirationRiskCategories.length) {
    const category = String(getAnalyticsField(userData, 'trialExpirationRiskCategory') || '');
    if (!engagement.trialExpirationRiskCategories.includes(category)) return false;
  }

  return true;
}

export function matchesAudience(userData = {}, showTo = {}) {
  const audience = String(showTo?.audience || 'all');

  if (audience === 'admins') {
    return isAdminUser(userData);
  }

  if (audience === 'specificUsers') {
    return matchesSpecificUsers(userData, showTo);
  }

  return matchesAccountStatus(userData, showTo.accountStatus || {})
    && matchesSubscription(userData, showTo.subscription || {})
    && matchesEngagementCriteria(userData, showTo.engagement || {});
}

export function summarizeAudience(showTo = {}) {
  const audience = String(showTo?.audience || 'all');
  if (audience === 'admins') return 'Admins only';
  if (audience === 'specificUsers') {
    const count = normalizeIdList(showTo.userIds).length + normalizeEmailList(showTo.emails).length;
    return `Specific users (${count})`;
  }

  const parts = [];
  const account = showTo.accountStatus || {};
  if (account.includeFree) parts.push('Free');
  if (account.includeTrial) parts.push('Trial');
  if (account.includePremium) parts.push('Premium');

  const sub = showTo.subscription || {};
  if (sub.includeMonthly) parts.push('Monthly');
  if (sub.includeYearly) parts.push('Yearly');
  if (sub.includeSiteLicense) parts.push('Site license');
  if (sub.includeLifetime) parts.push('Lifetime');
  if (sub.includeComped) parts.push('Comped');

  return parts.length ? parts.join(', ') : 'All users';
}

export function parseListInput(value) {
  if (Array.isArray(value)) return value;
  return String(value || '')
    .split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}
